import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    LegendComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import { format, parseISO } from 'date-fns';
import type {
    DatasetComponentOption,
    GridComponentOption,
    BarSeriesOption,
    TitleComponentOption,
    TooltipComponentOption,
    LegendComponentOption,
} from 'echarts';

type ECOption = echarts.ComposeOption<
    | BarSeriesOption
    | TitleComponentOption
    | TooltipComponentOption
    | LegendComponentOption
    | GridComponentOption
    | DatasetComponentOption
>;

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    BarChart,
    LegendComponent,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

interface FunnelBucket {
    period: string;
    period_start: string;
    visits?: number;
    registered?: number;
    updated_profile?: number;
}

interface Props {
    data: FunnelBucket[];
    period: 'daily' | 'weekly' | 'monthly' | 'quarterly';
}

const formatPeriodLabel = (date: Date, period: Props['period'], fallback: string) => {
    switch (period) {
        case 'daily':
            return format(date, 'MMM d');
        case 'weekly':
            return `W${format(date, 'II')} ${format(date, 'yyyy')}`;
        case 'monthly':
            return format(date, 'MMM yyyy');
        case 'quarterly':
            return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
        default:
            return fallback;
    }
};

const CustomerProfileFunnelTrendChart: React.FC<Props> = ({ data, period }) => {
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        if (!data || data.length === 0) {
            if (chartInstance.current && chartRef.current) {
                chartInstance.current.setOption({ series: [] });
            }
            return;
        }

        if (chartRef.current) {
            if (!chartInstance.current) {
                chartInstance.current = echarts.init(chartRef.current);
            }

            const categories: string[] = [];
            const visits: number[] = [];
            const registered: number[] = [];
            const updatedProfile: number[] = [];

            data.forEach(item => {
                let date: Date;
                try {
                    date = item.period_start ? parseISO(item.period_start) : new Date();
                    if (isNaN(date.getTime())) {
                        date = new Date(item.period_start);
                    }
                } catch {
                    date = new Date();
                }

                categories.push(formatPeriodLabel(date, period, item.period || ''));
                visits.push(Number(item.visits) || 0);
                registered.push(Number(item.registered) || 0);
                updatedProfile.push(Number(item.updated_profile) || 0);
            });

            const conversionLabel = (params: any, denominator: number) => {
                const value = Number(params.value) || 0;
                const rate = denominator > 0 ? ((value / denominator) * 100).toFixed(1) : '0.0';
                return `${value.toLocaleString()}\n${rate}%`;
            };

            const option: ECOption = {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                    formatter: (params: any) => {
                        if (!Array.isArray(params)) {
                            return '';
                        }

                        const index = params[0]?.dataIndex || 0;
                        const visitCount = visits[index] || 0;
                        const registeredCount = registered[index] || 0;
                        const updatedCount = updatedProfile[index] || 0;
                        const registrationRate = visitCount > 0 ? ((registeredCount / visitCount) * 100).toFixed(1) : '0.0';
                        const profileRate = registeredCount > 0 ? ((updatedCount / registeredCount) * 100).toFixed(1) : '0.0';

                        return [
                            params[0]?.axisValue || '',
                            `Visited: ${visitCount.toLocaleString()}`,
                            `Registered: ${registeredCount.toLocaleString()} (${registrationRate}% of visits)`,
                            `Updated Profile: ${updatedCount.toLocaleString()} (${profileRate}% of registered)`,
                        ].join('<br/>');
                    },
                },
                legend: {
                    data: ['Visited', 'Registered', 'Updated Profile'],
                    bottom: 0,
                },
                grid: {
                    left: '6%',
                    right: '6%',
                    top: '16%',
                    bottom: 70,
                    containLabel: true,
                },
                xAxis: {
                    type: 'category',
                    data: categories,
                    axisLabel: { rotate: period === 'daily' ? 45 : 0 },
                },
                yAxis: {
                    type: 'value',
                    name: 'Distinct People',
                },
                series: [
                    {
                        name: 'Visited',
                        type: 'bar',
                        data: visits,
                        itemStyle: { color: '#1890ff' },
                        barMaxWidth: 30,
                        label: {
                            show: true,
                            position: 'top',
                            formatter: (params: any) => Number(params.value || 0).toLocaleString(),
                            fontSize: 11,
                        },
                    },
                    {
                        name: 'Registered',
                        type: 'bar',
                        data: registered,
                        itemStyle: { color: '#52c41a' },
                        barMaxWidth: 30,
                        label: {
                            show: true,
                            position: 'top',
                            formatter: (params: any) => conversionLabel(params, visits[params.dataIndex] || 0),
                            fontSize: 11,
                        },
                    },
                    {
                        name: 'Updated Profile',
                        type: 'bar',
                        data: updatedProfile,
                        itemStyle: { color: '#722ed1' },
                        barMaxWidth: 30,
                        label: {
                            show: true,
                            position: 'top',
                            formatter: (params: any) => conversionLabel(params, registered[params.dataIndex] || 0),
                            fontSize: 11,
                        },
                    },
                ],
            };

            chartInstance.current.setOption(option);
        }

        const resizeHandler = () => chartInstance.current?.resize();
        window.addEventListener('resize', resizeHandler);
        return () => {
            window.removeEventListener('resize', resizeHandler);
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, [data, period]);

    return <div ref={chartRef} style={{ width: '100%', height: 380 }} />;
};

export default CustomerProfileFunnelTrendChart;
